import { User } from '@nextforce/api'
import { onlineManager } from '@tanstack/react-query'
import { navigationRef } from 'app/linking'
import { clearPage, getPage } from 'app/server/device_token'
import { useAuthentication } from 'authentication/context'
import { Route } from 'home/stack/types'
import Lottie from 'lottie-react-native'
import { navigateToPage } from 'onboarding/screens/auth'
import React, { useCallback, useEffect, useRef } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useScheduleContext } from 'schedule/schedule_context'
import { NoInternetText } from 'shared/components/no_internet_message'
import { SupportButton } from 'shared/components/support_button'
import { useStoredState } from 'shared/hooks/stored_state'
import { HomeItem } from '../types'
import { TileList } from './tile_list'
import { ButtonBarTile } from './tiles/button_bar'

interface Props {
  routes: Route[]
  items: HomeItem[] | undefined
  onButtonPress(route: Route, params?: object): void
  user?: User
  welcomeText: string
}

type ScreenEffectStatus = 'played' | 'not_played' | null

export function HomeView({
  routes,
  items,
  onButtonPress,
  user,
  welcomeText,
}: Props) {
  const lottieRef = useRef<Lottie>(null)
  const { setSchedule } = useScheduleContext()
  const [showScreenEffectStatus, setShowScreenEffectStatus] =
    useStoredState<ScreenEffectStatus>('show_screen_effects')
  const { status } = useAuthentication()
  const loggedIn = status === 'loggedIn'
  const isOffline = !onlineManager.isOnline()
  const onReplayEffect = () => {
    lottieRef.current?.play()
  }
  const isWeb = Platform.OS === 'web'
  const isUserWebForCrew = isWeb && !!user?.webForCrew
  const getScreenEffect = useCallback(() => {
    if (isWeb) {
      return
    }

    switch (user?.customScreenEffect) {
      case 'confetti':
        return require('assets/screen_effects/confetti.json')
      case 'balloons':
        return require('assets/screen_effects/balloons.json')
      case 'fireworks':
        return require('assets/screen_effects/fireworks.json')
    }
  }, [isWeb, user?.customScreenEffect])

  useEffect(() => {
    const initializeNavigation = async () => {
      if (loggedIn) {
        try {
          const page = await getPage()
          if (page) {
            if (page === 'my_shifts') {
              setSchedule({ calendar: 'helper' })
            } else if (page === 'next_shifts') {
              setSchedule({ calendar: 'helper', openShiftsOnly: true })
            }

            navigateToPage(navigationRef, page)
            await clearPage()
          }
        } catch (error) {
          console.error(error)
          await clearPage()
        }
      }
    }
    if (Platform.OS === 'web') {
      initializeNavigation().catch(() => null)
    }
  }, [loggedIn, setSchedule])

  useEffect(() => {
    if (user?.customScreenEffect && showScreenEffectStatus === null) {
      setShowScreenEffectStatus('not_played')
    }
  }, [
    setShowScreenEffectStatus,
    showScreenEffectStatus,
    user?.customScreenEffect,
  ])

  useEffect(() => {
    if (showScreenEffectStatus === 'not_played') {
      lottieRef.current?.play()
      setShowScreenEffectStatus('played')
    }
  }, [setShowScreenEffectStatus, showScreenEffectStatus])

  useEffect(() => {
    if (!user?.customScreenEffect) {
      setShowScreenEffectStatus(null)
    }
  }, [setShowScreenEffectStatus, user?.customScreenEffect])

  if (!items) {
    return null
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.safeArea}>
      {getScreenEffect() ? (
        <Lottie
          style={styles.lottie}
          ref={lottieRef}
          webStyle={styles.lottie}
          source={getScreenEffect()}
          autoPlay={false}
          loop={false}
          resizeMode={'cover'}
          onAnimationFinish={() => setShowScreenEffectStatus('played')}
        />
      ) : null}
      <View style={styles.container}>
        <ButtonBarTile routes={routes} onButtonPress={onButtonPress} />
        <TileList
          items={items}
          onTilePress={onButtonPress}
          user={user}
          welcomeText={welcomeText}
          onReplayEffect={onReplayEffect}
        />
        {!isUserWebForCrew ? (
          <View style={styles.floatingView}>
            <SupportButton disabled={isOffline} />
          </View>
        ) : null}
      </View>
      {isOffline ? <NoInternetText /> : null}
    </SafeAreaView>
  )
}

export const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
    marginTop: 16,
    justifyContent: 'flex-start',
  },
  floatingView: {
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
  noConnection: {
    textAlign: 'center',
    fontStyle: 'italic',
    fontSize: 16,
  },
  lottie: {
    zIndex: -10,
    width: '100%',
    height: '100%',
    position: 'absolute',
  },
})
